import { formatMoney } from '@theme/money-formatting';

/**
 * "Consultar frete e prazo" no carrinho lateral.
 *
 * Usa a API de frete do carrinho da Shopify (mesmas opções/prazos do checkout):
 * POST /cart/prepare_shipping_rates.json → GET /cart/async_shipping_rates.json (polling até ficar pronto).
 * O estado (province) vem do ViaCEP, porque as zonas de frete do Brasil são por estado.
 *
 * O drawer é re-renderizado (morph) a cada alteração do carrinho, o que limpa o campo e o resultado;
 * por isso o CEP consultado fica salvo e a cotação é refeita depois de cada atualização do carrinho.
 */

const STORAGE_KEY = 'pasquini:shipping-consult-cep';
const POLL_INTERVAL = 700;
const POLL_ATTEMPTS = 15;

class CartShippingConsult extends HTMLElement {
  /** @type {AbortController | null} */
  #request = null;

  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('input', this.#onInput);
    document.addEventListener('shopify:cart:lines-update', this.#onCartUpdate);

    const savedCep = this.#savedCep;
    if (savedCep && this.#input) this.#input.value = formatCep(savedCep);
  }

  disconnectedCallback() {
    this.#request?.abort();
    this.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('input', this.#onInput);
    document.removeEventListener('shopify:cart:lines-update', this.#onCartUpdate);
  }

  get #input() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="cep"]'));
  }

  get #button() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('button[type="submit"]'));
  }

  get #results() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-shipping-results]'));
  }

  get #savedCep() {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /** @param {string | null} cep */
  set #savedCep(cep) {
    try {
      if (cep) sessionStorage.setItem(STORAGE_KEY, cep);
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch {}
  }

  /** @param {Event} event */
  #onInput = (event) => {
    const input = this.#input;
    if (event.target !== input || !input) return;
    input.value = formatCep(input.value);
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    event.preventDefault();
    const cep = (this.#input?.value || '').replace(/\D/g, '');
    this.#quote(cep);
  };

  /** Re-quotes after the drawer morph wiped the field/results (and the cart total changed). */
  #onCartUpdate = (/** @type {Event & { promise?: Promise<unknown> }} */ event) => {
    const cep = this.#savedCep;
    if (!cep) return;

    event.promise
      ?.then(() => new Promise((resolve) => setTimeout(resolve, 50)))
      .then(() => {
        if (!this.isConnected) return;
        if (this.#input) this.#input.value = formatCep(cep);
        this.#quote(cep);
      })
      .catch(() => {});
  };

  /** @param {string} cep - 8 digits */
  async #quote(cep) {
    if (cep.length !== 8) {
      this.#renderMessage(this.dataset.textInvalid || 'Digite um CEP válido.', true);
      return;
    }

    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    const { signal } = request;

    this.#setLoading(true);
    this.#renderMessage(this.dataset.textLoading || 'Calculando frete…');

    try {
      const province = await fetchProvince(cep, signal);
      const params = new URLSearchParams({
        'shipping_address[zip]': formatCep(cep),
        'shipping_address[country]': 'Brazil',
      });
      if (province) params.set('shipping_address[province]', province);

      const prepare = await fetch(`${Theme.routes.cart_url}/prepare_shipping_rates.json?${params}`, {
        method: 'POST',
        signal,
      });

      if (!prepare.ok) {
        const errors = await prepare.json().catch(() => null);
        throw new ShippingError(firstError(errors));
      }

      const rates = await pollRates(params, signal);
      this.#savedCep = cep;
      this.#renderRates(rates);
    } catch (error) {
      if (/** @type {Error} */ (error)?.name === 'AbortError') return;
      const message =
        error instanceof ShippingError && error.message
          ? error.message
          : this.dataset.textError || 'Não foi possível calcular o frete. Tente novamente.';
      this.#renderMessage(message, true);
    } finally {
      if (this.#request === request) {
        this.#request = null;
        this.#setLoading(false);
      }
    }
  }

  /** @param {boolean} loading */
  #setLoading(loading) {
    const button = this.#button;
    if (!button) return;
    button.disabled = loading;
    button.setAttribute('aria-busy', String(loading));
  }

  /**
   * @param {string} message
   * @param {boolean} [isError]
   */
  #renderMessage(message, isError = false) {
    const results = this.#results;
    if (!results) return;

    const paragraph = document.createElement('p');
    paragraph.className = `cart-shipping-consult__message${isError ? ' cart-shipping-consult__message--error' : ''}`;
    paragraph.textContent = message;

    results.replaceChildren(paragraph);
    results.hidden = false;
  }

  /** @param {ShippingRate[]} rates */
  #renderRates(rates) {
    const results = this.#results;
    if (!results) return;

    if (!rates.length) {
      this.#renderMessage(this.dataset.textEmpty || 'Nenhuma opção de entrega disponível para este CEP.', true);
      return;
    }

    const list = document.createElement('ul');
    list.className = 'cart-shipping-consult__rates';
    list.setAttribute('role', 'list');

    for (const rate of [...rates].sort((a, b) => Number(a.price) - Number(b.price))) {
      const item = document.createElement('li');
      item.className = 'cart-shipping-consult__rate';

      const info = document.createElement('span');
      info.className = 'cart-shipping-consult__rate-info';

      const name = document.createElement('span');
      name.className = 'cart-shipping-consult__rate-name';
      name.textContent = rate.presentment_name || rate.name;
      info.append(name);

      const deliveryText = this.#deliveryText(rate.delivery_days);
      if (deliveryText) {
        const delivery = document.createElement('span');
        delivery.className = 'cart-shipping-consult__rate-delivery';
        delivery.textContent = deliveryText;
        info.append(delivery);
      }

      const price = document.createElement('span');
      price.className = 'cart-shipping-consult__rate-price';
      const cents = Math.round(Number(rate.price) * 100);
      price.textContent =
        cents === 0
          ? this.dataset.textFree || 'Grátis'
          : formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'BRL');

      item.append(info, price);
      list.append(item);
    }

    results.replaceChildren(list);
    results.hidden = false;
  }

  /** @param {number[] | undefined} days */
  #deliveryText(days) {
    if (!Array.isArray(days) || !days.length) return '';
    const [min, max = min] = days;
    if (!max) return '';
    if (min === max) return (this.dataset.textDeliverySingle || 'Até [days] dias úteis').replace('[days]', String(max));
    return (this.dataset.textDeliveryRange || '[min] a [max] dias úteis')
      .replace('[min]', String(min))
      .replace('[max]', String(max));
  }
}

/**
 * @typedef {{ name: string, presentment_name?: string, price: string, delivery_days?: number[] }} ShippingRate
 */

class ShippingError extends Error {}

/** @param {string} value */
function formatCep(value) {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
}

/**
 * Shopify's shipping zones for Brazil are per state, so resolve it from the CEP.
 * @param {string} cep
 * @param {AbortSignal} signal
 * @returns {Promise<string | null>}
 */
async function fetchProvince(cep, signal) {
  try {
    const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`, { signal });
    const data = await response.json();
    if (data?.erro) throw new ShippingError('CEP não encontrado.');
    return data?.estado || data?.uf || null;
  } catch (error) {
    if (error instanceof ShippingError || /** @type {Error} */ (error)?.name === 'AbortError') throw error;
    // ViaCEP fora do ar: tenta só com o CEP
    return null;
  }
}

/**
 * @param {URLSearchParams} params
 * @param {AbortSignal} signal
 * @returns {Promise<ShippingRate[]>}
 */
async function pollRates(params, signal) {
  for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
    const response = await fetch(`${Theme.routes.cart_url}/async_shipping_rates.json?${params}`, { signal });

    if (response.status === 200) {
      const data = await response.json();
      if (data && Array.isArray(data.shipping_rates)) return data.shipping_rates;
    } else if (response.status !== 202) {
      const errors = await response.json().catch(() => null);
      throw new ShippingError(firstError(errors));
    }

    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
  }

  throw new Error('Shipping rates timeout');
}

/**
 * Shopify returns errors as `{ field: ["message"] }`, in English — map to a PT-BR message instead.
 * @param {any} errors
 * @returns {string}
 */
function firstError(errors) {
  if (!errors || typeof errors !== 'object') return '';
  if ('zip' in errors) return 'CEP inválido ou sem entrega disponível.';
  return '';
}

if (!customElements.get('cart-shipping-consult')) {
  customElements.define('cart-shipping-consult', CartShippingConsult);
}
